import { z } from 'zod'
import { uuidSchema } from '../validators/uuid.validator.js'
import { parseTaxRatePercent } from '../helpers/tax.js'
import { seasonStartMonthsSchema } from '../validators/season-start-months.validator.js'
import {
  isLicenseState,
  isOrganizationPhone,
  organizationNameSchema,
  proposalValidityDaysSchema,
  seasonReminderLeadDaysSchema,
  toLicenseState,
  toOrganizationPhone,
  type LicenseState,
  type OrganizationPhone,
} from '../validators/organization-fields.validator.js'

/**
 * A dollar rate accepted from a client.
 *
 * A string, so the exact value crosses the wire and reaches the `Decimal`
 * column without passing through a float. Bounded to eight digits because a
 * five-figure hourly rate is a typo, and one that got through would quietly
 * multiply into every quote on the site.
 *
 * Zero is allowed. "We are not charging for labor on this" is a decision
 * someone may legitimately record, and it is distinguishable from null, which
 * means no rate has been set at all.
 */
const rateSchema = z
  .string()
  .trim()
  // The empty alternative is load-bearing, not decoration. Without it the
  // regex rejects "" before the transform below ever runs, so a form that
  // clears the field by sending an empty string gets a 400 instead of clearing
  // the value — and the transform reads as though it handles a case it cannot
  // reach.
  .regex(
    /^$|^\d{1,8}(\.\d{1,2})?$/,
    'Enter a rate in dollars, e.g. 85 or 85.50',
  )
  .transform((value) => (value === '' ? null : value))
  .nullish()

/**
 * A sales-tax percent accepted from a client.
 *
 * A string, like the labor rate, so "8.25" is stored as exactly that rather
 * than as the nearest float. Three decimal places because combined rates
 * land on thousandths. Above 100 is refused: a rate of 825 is someone typing
 * the percent in the wrong place, and it would quietly add itself to every
 * proposal.
 *
 * Zero is allowed. "We charge no tax" is a decision someone may record, and
 * it is distinguishable from null, which means no rate has been set at all.
 * On a proposal the two print the same — no tax line — which is the point.
 */
const taxRateSchema = z
  .string()
  .trim()
  // Empty clears the rate — see the note on `rateSchema`.
  .regex(/^$|^\d{1,3}(\.\d{1,3})?$/, 'Enter a tax rate as a percent, e.g. 8.25')
  .refine(
    (value) => value === '' || parseTaxRatePercent(value) !== null,
    'Enter a tax rate as a percent, up to 100, e.g. 8.25',
  )
  .transform((value) => (value === '' ? null : value))
  .nullish()

/**
 * A coverage limit accepted from a client.
 *
 * Same treatment as the rate — a string all the way to the `Decimal` column —
 * and the same eight-digit bound, which comfortably holds the ten-million-dollar
 * umbrella policy that is the top of the realistic range and rejects the
 * mistyped one that is not.
 */
const coverageSchema = z
  .string()
  .trim()
  // Empty clears the limit — see the note on `rateSchema`.
  .regex(/^$|^\d{1,8}(\.\d{1,2})?$/, 'Enter an amount in dollars, e.g. 2000000')
  .transform((value) => (value === '' ? null : value))
  .nullish()

/**
 * A short identifier: a license number, a certification number, a carrier.
 *
 * Trimmed, and blank becomes null rather than an empty string. A credential
 * recorded as "" would print as a labeled empty row on a proposal, which reads
 * worse than not claiming it at all.
 */
const identifierSchema = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((value) => (value === '' ? null : value))
    .nullish()

/**
 * Settings are updated as a partial patch — every field optional, absent means
 * "leave it alone".
 *
 * Note the fields carry no `.default()`. A default here would survive
 * `.partial()` and arrive as a value the caller never sent, which on a settings
 * record means one screen quietly resetting another screen's setting. See the
 * note in `tree.input.ts`, where exactly that shipped.
 */
export const updateOrganizationInputSchema = z
  .object({
    /**
     * Required when sent, because a blank business name has nowhere sensible
     * to render. Absent is fine — this is a patch — but empty is not.
     */
    name: z.string().trim().pipe(organizationNameSchema).optional(),

    treeLaborRatePerHour: rateSchema,
    taxRatePercent: taxRateSchema,

    insuranceCarrier: identifierSchema(200),
    generalLiabilityPerOccurrence: coverageSchema,
    generalLiabilityAggregate: coverageSchema,
    autoLiabilityPerAccident: coverageSchema,
    clientsMaySelectJobTypes: z.boolean().optional(),
    requireItemizedEstimates: z.boolean().optional(),
    /** Days a price holds after sending. Null means no expiry. */
    proposalValidityDays: proposalValidityDaysSchema.nullish(),
    /**
     * The month each season starts in. Sent whole, because no one month can
     * be checked without the other three.
     */
    seasonStartMonths: seasonStartMonthsSchema.optional(),
    /** Days before a season starts that its reminders are due. */
    seasonReminderLeadDays: seasonReminderLeadDaysSchema.optional(),
    carriesWorkersCompensation: z.boolean().optional(),
    providesAdditionalInsured: z.boolean().optional(),
    isaCertificationNumber: identifierSchema(60),
    contractorLicenseNumber: identifierSchema(60),
    /** Printed on proposals that bid irrigation: "LI0023791", "BP0023977". */
    irrigatorLicenseNumber: identifierSchema(60),
    backflowTesterLicenseNumber: identifierSchema(60),
    /**
     * The number a client should call, as the business writes it, e.g.
     * "(806) 794-1300". Shown on proposals beside the Accept button.
     */
    phone: z
      .string()
      .trim()
      .refine(
        (value) => value === '' || isOrganizationPhone(value),
        'Enter a phone number, e.g. (806) 794-1300',
      )
      .transform((value): OrganizationPhone | null =>
        value === '' ? null : toOrganizationPhone(value),
      )
      .nullish(),
    licenseState: z
      .string()
      .trim()
      .toUpperCase()
      .refine(
        (value) => value === '' || isLicenseState(value),
        'Enter a two-letter state, e.g. TX',
      )
      .transform((value): LicenseState | null =>
        value === '' ? null : toLicenseState(value),
      )
      .nullish(),
    /**
     * The id of an uploaded logo, or null to remove the one in use.
     *
     * An id rather than the file: the browser uploads through the normal
     * presigned path first and sends the resulting document id here, so this
     * endpoint never handles bytes.
     */
    logoDocumentId: uuidSchema.nullable().optional(),
  })
  .strict()

export type UpdateOrganizationInput = z.infer<
  typeof updateOrganizationInputSchema
>
